import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Login.css";

const SG_TIME_ZONE = "Asia/Singapore";

async function fetchServerNowUtc() {
  try {
    const res = await fetch(`${window.location.origin}/`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h);
  } catch {}

  try {
    const res = await fetch(`${window.location.origin}/favicon.ico`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h);
  } catch {}

  return new Date();
}

function getSgYmd(date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SG_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const values = {};
  parts.forEach((part) => {
    if (part.type !== "literal") values[part.type] = part.value;
  });

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
  };
}

// Pawn expiry rule:
// - Normally: same calendar day 6 months later, minus 1 day.
// - If that day does not exist in the target month (e.g. 29/30/31 Aug -> Feb),
//   use the target month's final day instead.
function computeExpiryYmd(date) {
  const { year, month, day } = getSgYmd(date);

  const targetMonthStart = new Date(Date.UTC(year, month - 1 + 6, 1));
  const targetYear = targetMonthStart.getUTCFullYear();
  const targetMonth = targetMonthStart.getUTCMonth();
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();

  if (day > lastDay) {
    return { year: targetYear, month: targetMonth + 1, day: lastDay };
  }

  const expiry = new Date(Date.UTC(targetYear, targetMonth, day));
  expiry.setUTCDate(expiry.getUTCDate() - 1);

  return {
    year: expiry.getUTCFullYear(),
    month: expiry.getUTCMonth() + 1,
    day: expiry.getUTCDate(),
  };
}

function formatYmdGb({ year, month, day }) {
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
}

const Login = () => {
  const navigate = useNavigate();

  // Form state
  const [staffCode, setStaffCode] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState("staff"); // Default: Staff
  const [error, setError] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [expiryDate, setExpiryDate] = useState("");

  useEffect(() => {
    let interval;
    let mounted = true;

    (async () => {
      const serverNow = await fetchServerNowUtc();
      const offsetMs = serverNow.getTime() - Date.now();

      const update = () => {
        const correctedNow = new Date(Date.now() + offsetMs);
        if (!mounted) return;

        setCurrentDate(correctedNow);
        setExpiryDate(formatYmdGb(computeExpiryYmd(correctedNow)));
      };

      update();
      interval = setInterval(update, 1000);
    })();

    return () => {
      mounted = false;
      if (interval) clearInterval(interval);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!staffCode || !password) {
      setError("Please enter both Staff Code and Password.");
      return;
    }

    try {
      if (userType === "staff") {
        // ✅ Send login request to backend with session handling
        const response = await axios.post("https://www.lianfoong.com/api/staff/login", {
          staffCode,
          staffPassword: password, // Ensure password is passed correctly
        }, { withCredentials: true }); // ✅ Include credentials for session cookies

        if (response.status === 200) {
          // ✅ Retrieve session data to confirm login
          const sessionResponse = await axios.get("https://www.lianfoong.com/api/staff/session", { withCredentials: true });

          if (sessionResponse.data) {
            console.log("Logged in as:", sessionResponse.data);
            navigate("/dashboard"); // ✅ Redirect after successful login
          } else {
            setError("Login failed, please try again.");
          }
        }
      } else {
        // ✅ Redirect Management login (if needed)
        navigate("/management-dashboard");
      }
    } catch (err) {
      setError("Login failed. Check credentials.");
    }
  };

  return (
    <div className="login-page">
      <div className="main-headers">
        <h1>LIAN FOONG PAWNSHOP</h1>
        <h2>Lian Foong Pawnshop Pte Ltd</h2>
        <div className="date-time-container">
          <div className="date-box">DATE: {currentDate.toLocaleDateString("en-GB", { timeZone: SG_TIME_ZONE })}</div>
          <div className="expiry-box">EXPIRY: {expiryDate}</div>
          <div className="time-box">TIME: {currentDate.toLocaleTimeString("en-GB", { timeZone: SG_TIME_ZONE })}</div>
        </div>
      </div>
      <div className="login-container">
        <h2 className="login-title">Login</h2>

        {error && <p className="error-message">{error}</p>}

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>User Type:</label>
            <select className="input-field" value={userType} onChange={(e) => setUserType(e.target.value)}>
              <option value="staff">Staff</option>
              <option value="management">Management</option>
            </select>
          </div>

          <div className="form-group">
            <label>Staff Code:</label>
            <input type="text" className="input-field" value={staffCode} onChange={(e) => setStaffCode(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Password:</label>
            <input type="password" className="input-field" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>

          <div className="button-group">
            <button type="submit" className="login-button">Login</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
